import React from "react";

const toText = (value, fallback = "-") => {
  const text = String(value ?? "").trim();
  return text || fallback;
};

const formatDateTime = (value) => {
  if (!value) return "-";

  try {
    const date = new Date(value);
    const datePart = new Intl.DateTimeFormat(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
    const timePart = new Intl.DateTimeFormat(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
      .format(date)
      .replace(":", ".")
      .replace(/\s+/g, " ")
      .trim();

    return `${datePart} ${timePart}`;
  } catch {
    return String(value);
  }
};

const formatDateOnly = (value) => {
  if (!value) return "-";

  try {
    return new Intl.DateTimeFormat(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(value));
  } catch {
    return String(value);
  }
};

export function TicketTaskDetailsPdfDocument({
  components,
  logoUrl,
  companyInfo,
  report,
  generatedAt,
}) {
  const { Document, Page, StyleSheet, Text, View, Image } = components;

  const styles = StyleSheet.create({
    page: {
      paddingTop: 22,
      paddingBottom: 24,
      paddingHorizontal: 24,
      backgroundColor: "#f8fafc",
      fontSize: 9.5,
      color: "#0f172a",
    },
    headerBand: {
      backgroundColor: "#0f172a",
      borderRadius: 12,
      paddingVertical: 14,
      paddingHorizontal: 16,
      marginBottom: 9,
    },
    headerTopRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: 4,
    },
    brandWrap: {
      flexDirection: "row",
      alignItems: "center",
      maxWidth: "62%",
    },
    logo: {
      width: 44,
      height: 44,
      objectFit: "contain",
      marginRight: 10,
      backgroundColor: "#ffffff",
      borderRadius: 10,
      padding: 4,
    },
    brandEyebrow: {
      fontSize: 8,
      color: "#94a3b8",
      letterSpacing: 0.8,
      textTransform: "uppercase",
      marginBottom: 2,
    },
    companyName: {
      fontSize: 15,
      fontWeight: 700,
      color: "#ffffff",
    },
    headerContactWrap: {
      maxWidth: "38%",
      alignItems: "flex-end",
      backgroundColor: "#111f36",
      borderWidth: 1,
      borderColor: "#24344e",
      borderRadius: 8,
      paddingVertical: 6,
      paddingHorizontal: 8,
    },
    headerContactText: {
      fontSize: 8.1,
      color: "#e2e8f0",
      lineHeight: 1.4,
      textAlign: "right",
      marginBottom: 1,
    },
    headerContactAddress: {
      fontSize: 7.8,
      color: "#cbd5e1",
      lineHeight: 1.3,
      textAlign: "right",
      marginBottom: 3,
    },
    headerContactLabel: {
      fontSize: 7.4,
      color: "#94a3b8",
      textTransform: "uppercase",
      letterSpacing: 0.5,
      marginBottom: 1,
    },
    section: {
      marginBottom: 8,
    },
    sectionTitle: {
      fontSize: 11.5,
      fontWeight: 700,
      color: "#0f172a",
      marginBottom: 4,
    },
    infoTable: {
      backgroundColor: "#ffffff",
      borderWidth: 1,
      borderColor: "#cbd5e1",
      borderRadius: 8,
      overflow: "hidden",
    },
    infoRow: {
      flexDirection: "row",
      borderBottomWidth: 1,
      borderBottomColor: "#e2e8f0",
      minHeight: 24,
    },
    infoRowLast: {
      borderBottomWidth: 0,
    },
    infoLabelCell: {
      width: "24%",
      backgroundColor: "#f8fafc",
      paddingVertical: 4,
      paddingHorizontal: 7,
      borderRightWidth: 1,
      borderRightColor: "#e2e8f0",
    },
    infoValueCell: {
      width: "26%",
      paddingVertical: 4,
      paddingHorizontal: 7,
      borderRightWidth: 1,
      borderRightColor: "#e2e8f0",
    },
    infoLabelCellSecond: {
      width: "24%",
      backgroundColor: "#f8fafc",
      paddingVertical: 4,
      paddingHorizontal: 7,
      borderRightWidth: 1,
      borderRightColor: "#e2e8f0",
    },
    infoValueCellSecond: {
      width: "26%",
      paddingVertical: 4,
      paddingHorizontal: 7,
    },
    infoLabelText: {
      fontSize: 7.7,
      fontWeight: 700,
      color: "#334155",
      textTransform: "uppercase",
      letterSpacing: 0.5,
    },
    infoValueText: {
      fontSize: 8.9,
      color: "#0f172a",
      lineHeight: 1.3,
    },
    infoValueStrong: {
      fontSize: 8.9,
      fontWeight: 700,
      color: "#0f172a",
      lineHeight: 1.3,
    },
    table: {
      backgroundColor: "#ffffff",
      borderWidth: 1,
      borderColor: "#cbd5e1",
      borderRadius: 10,
      overflow: "hidden",
    },
    tableHeader: {
      flexDirection: "row",
      backgroundColor: "#0f172a",
      borderBottomWidth: 1,
      borderBottomColor: "#1e293b",
      paddingVertical: 7,
    },
    tableHeaderText: {
      fontSize: 7.6,
      fontWeight: 700,
      color: "#e2e8f0",
      textTransform: "uppercase",
      letterSpacing: 0.45,
      paddingHorizontal: 7,
    },
    colTask: { width: "30%" },
    colAssigned: { width: "18%" },
    colPriority: { width: "12%" },
    colStatus: { width: "14%" },
    colDueDate: { width: "14%" },
    colProgress: { width: "12%" },
    tableRow: {
      flexDirection: "row",
      borderBottomWidth: 1,
      borderBottomColor: "#e2e8f0",
      paddingVertical: 6,
    },
    tableRowAlt: {
      backgroundColor: "#f8fafc",
    },
    tableRowLast: {
      borderBottomWidth: 0,
    },
    cellText: {
      fontSize: 8.1,
      color: "#0f172a",
      lineHeight: 1.35,
      paddingHorizontal: 7,
    },
    taskTitle: {
      fontSize: 8.3,
      fontWeight: 700,
      color: "#0f172a",
      lineHeight: 1.35,
      paddingHorizontal: 7,
      marginBottom: 2,
    },
    taskDescription: {
      fontSize: 7.6,
      color: "#475569",
      lineHeight: 1.3,
      paddingHorizontal: 7,
    },
    emptyState: {
      backgroundColor: "#ffffff",
      borderWidth: 1,
      borderColor: "#cbd5e1",
      borderRadius: 8,
      paddingVertical: 14,
      paddingHorizontal: 14,
    },
    emptyStateText: {
      fontSize: 9,
      color: "#475569",
      lineHeight: 1.5,
      textAlign: "center",
    },
    footer: {
      marginTop: 6,
      paddingTop: 6,
      borderTopWidth: 1,
      borderTopColor: "#cbd5e1",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    },
    footerText: {
      fontSize: 8,
      color: "#64748b",
    },
  });

  const ticketInfoRows = [
    [
      { label: "Ticket No", value: toText(report?.ticketNumber) },
      { label: "Open Date", value: formatDateOnly(report?.openDate) },
    ],
    [
      { label: "Ticket Name", value: toText(report?.ticketName) },
      { label: "Status", value: toText(report?.ticketStatus) },
    ],
    [
      { label: "Organization", value: toText(report?.organizationName) },
      { label: "Employee", value: toText(report?.employeeName, "Unassigned") },
    ],
    [
      { label: "Customer", value: toText(report?.customerName) },
      { label: "Customer User", value: toText(report?.customerUserName, "Unknown User") },
    ],
    [
      { label: "Task Count", value: String(report?.taskCount || 0) },
      { label: "Generated", value: formatDateTime(generatedAt) },
    ],
  ];

  return (
    <Document
      title="Ticket Task Details Report"
      author="BanglaFire CRM"
      subject="Ticket task details report"
      creator="BanglaFire CRM"
      producer="BanglaFire CRM"
    >
      <Page size="A4" style={styles.page}>
        <View style={styles.headerBand}>
          <View style={styles.headerTopRow}>
            <View style={styles.brandWrap}>
              {logoUrl ? <Image src={logoUrl} style={styles.logo} /> : null}
              <View>
                <Text style={styles.brandEyebrow}>Ticket Task Details Report</Text>
                <Text style={styles.companyName}>{toText(companyInfo?.name, "BanglaFire CRM")}</Text>
              </View>
            </View>
            <View style={styles.headerContactWrap}>
              <Text style={styles.headerContactAddress}>39/9 A, Senpara Parbata, Mirpur, Dhaka-1216</Text>
              <Text style={styles.headerContactLabel}>Mobile</Text>
              <Text style={styles.headerContactText}>(+880) 9639222999, 1709927934</Text>
              <Text style={styles.headerContactLabel}>Email</Text>
              <Text style={styles.headerContactText}>md@banglafire.com, support@banglafire.com</Text>
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Ticket Information</Text>
          <View style={styles.infoTable}>
            {ticketInfoRows.map((row, index) => (
              <View key={`${row[0].label}-${row[1].label}`} style={[styles.infoRow, index === ticketInfoRows.length - 1 ? styles.infoRowLast : null]}>
                <View style={styles.infoLabelCell}>
                  <Text style={styles.infoLabelText}>{row[0].label}</Text>
                </View>
                <View style={styles.infoValueCell}>
                  <Text style={row[0].label === "Task Count" ? styles.infoValueStrong : styles.infoValueText}>{row[0].value}</Text>
                </View>
                <View style={styles.infoLabelCellSecond}>
                  <Text style={styles.infoLabelText}>{row[1].label}</Text>
                </View>
                <View style={styles.infoValueCellSecond}>
                  <Text style={row[1].label === "Generated" ? styles.infoValueStrong : styles.infoValueText}>{row[1].value}</Text>
                </View>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Task List</Text>

          {report?.tasks?.length ? (
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={[styles.tableHeaderText, styles.colTask]}>Task</Text>
                <Text style={[styles.tableHeaderText, styles.colAssigned]}>Assigned</Text>
                <Text style={[styles.tableHeaderText, styles.colPriority]}>Priority</Text>
                <Text style={[styles.tableHeaderText, styles.colStatus]}>Status</Text>
                <Text style={[styles.tableHeaderText, styles.colDueDate]}>Due Date</Text>
                <Text style={[styles.tableHeaderText, styles.colProgress]}>Progress</Text>
              </View>
              {report.tasks.map((task, index) => (
                <View
                  key={`${task.taskId}-${index}`}
                  style={[
                    styles.tableRow,
                    index % 2 === 1 ? styles.tableRowAlt : null,
                    index === report.tasks.length - 1 ? styles.tableRowLast : null,
                  ]}
                  wrap={false}
                >
                  <View style={styles.colTask}>
                    <Text style={styles.taskTitle}>{toText(task.title)}</Text>
                    <Text style={styles.taskDescription}>{toText(task.description)}</Text>
                  </View>
                  <Text style={[styles.cellText, styles.colAssigned]}>{toText(task.assignedEmployeeName, "Unassigned")}</Text>
                  <Text style={[styles.cellText, styles.colPriority]}>{toText(task.priority)}</Text>
                  <Text style={[styles.cellText, styles.colStatus]}>{toText(task.status)}</Text>
                  <Text style={[styles.cellText, styles.colDueDate]}>{formatDateOnly(task.dueDate)}</Text>
                  <Text style={[styles.cellText, styles.colProgress]}>{`${Number(task.progressPercent || 0)}%`}</Text>
                </View>
              ))}
            </View>
          ) : (
            <View style={styles.emptyState}>
              <Text style={styles.emptyStateText}>
                No tasks were found for this ticket.
              </Text>
            </View>
          )}
        </View>

        <View style={styles.footer}>
          <Text style={styles.footerText}>Generated on {formatDateTime(generatedAt)}</Text>
          <Text style={styles.footerText}>BanglaFire CRM Professional Report</Text>
        </View>
      </Page>
    </Document>
  );
}
